import { sortBy } from "lodash";
import { ObjectiveService, ITreeNode, UnitRangeModel } from "@/services/objective-manager";
import { Component, Vue, Watch } from "vue-property-decorator";
import { ApplicationViews, TreeActionType, ObjectiveActionType } from "@/util/enum";
import { Tree, Icon, Button, Drawer, Alert } from "ant-design-vue";
import "ant-design-vue/dist/antd.less";
import UnitObjectiveView from "@/components/unit-objective-view/unit-objective-view.vue";
import UnitObjectiveCheckView from "@/components/unit-objective-check-view/unit-objective-check-view.vue";
import VersionGroupUnitsSelect from "@/components/version-group-units-select/version-group-units-select.vue";
import PageTitle from "@/components/page-title/page-title.vue";
import UnitManage from "./components/unit-manage/unit-manage.vue";
import ExpandCollapse from "./components/expand-collapse/expand-collapse.vue";
import Loading from "@/components/loading/loading.vue";
import { ObjectiveModule } from "@/modules/objective";
import { VersionGroupModel } from "@/services/version-group";
import RichTextPreviewer from "@/components/rich-text-previewer/rich-text-previewer.vue";
import { disabledDueToAccessLevel, getRouteAccessLevel, isAnyVersionSelected, pureRichText } from "@/util/func";
import { CommonLocale, ObjectiveListLocale } from "@/locales/localeid";
import { VersionGroupModule } from "@/modules/versiongroup";
import { fmtMsg } from "commonui";

@Component({
	components: {
		Tree,
		Icon,
		Button,
		Drawer,
		Loading,
		UnitObjectiveView,
		PageTitle,
		UnitManage,
		ExpandCollapse,
		RichTextPreviewer,
		VersionGroupUnitsSelect,
		Alert,
		UnitObjectiveCheckView
	}
})
export default class ObjectiveManagement extends Vue {
	public view = ApplicationViews.ObjectiveManager;
	public treedata: ITreeNode[] = [];
	public autoExpandParent = true;
	public expandedKeys: string[] = [];
	public showLine = true;
	public showIcon = true;
	public level1Keys: string[] = [];
	public level2Keys: string[] = [];
	public level3Keys: string[] = [];
	public unitObject: UnitRangeModel = {} as UnitRangeModel;
	public visible = false;
	public drawerTitle = "";
	public drawerNode = {};
	public isLoading = false;
	public ObjectiveActionType = ObjectiveActionType;
	public commonLocale = CommonLocale;
	public objectiveListLocale = ObjectiveListLocale;
	public noVersion: boolean = false;
	private versionGroupModule = VersionGroupModule; // For watch need to create local reference

	created() {
		if (!isAnyVersionSelected()) {
			this.noVersion = true;
		}
	}

	@Watch("versionGroupModule.selectedGsVersion")
	@Watch("versionGroupModule.selectedLsVersion")
	isVersionUpdated() {
		if (isAnyVersionSelected()) {
			this.noVersion = false;
		} else {
			this.noVersion = true;
		}
	}

	localizedString(id: string, params?: {}) {
		return fmtMsg(id, params);
	}

	mounted() {
		this.versionGroupModule.setDisabled(false);
	}

	destroyed() {
		this.versionGroupModule.setDisabled(true);
	}

	get vcsLevelUnits() {
		return sortBy(ObjectiveModule.vcsUnits, "unit");
	}

	get versionGroup(): VersionGroupModel | undefined {
		const versionGroupId =
			this.versionGroupModule.selectedGsVersion !== "-1"
				? this.versionGroupModule.selectedGsVersion
				: this.versionGroupModule.selectedLsVersion;
		return this.versionGroupModule.versionGroups.find((v) => v.id == versionGroupId);
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	@Watch("unitObject")
	getObjectives() {
		const params = {
			endUnitId: this.unitObject.endUnitId,
			startUnitId: this.unitObject.startUnitId,
			versionGroupId: this.versionGroup?.id
		};
		this.setLoading(true);
		ObjectiveService.getObjectivesTree(params).then((data) => {
			this.clearCache();
			this.formatTreeLevel(data.children, "");
			this.expandedKeys = this.level2Keys;
			this.autoExpandParent = true;
			ObjectiveModule.setLevel3Selected(true);
			this.setLoading(false);
			this.treedata = data.children;
		});
	}

	clearCache() {
		this.level1Keys = [];
		this.level2Keys = [];
		this.level3Keys = [];
		this.expandedKeys = [];
	}

	formatClass(item) {
		return `title-level-${item.level}`;
	}

	treeLevelSequence(parentIndex, index) {
		return `${parentIndex}${index + 1}.`;
	}

	formatTreeLevel(array, parentIndex): void {
		array.forEach((p, index) => {
			Object.assign(p, {
				key: p.id,
				title: p.text,
				text: pureRichText(p.text),
				show: false,
				showUpArrow: index != 0,
				showDownArrow: index != array.length - 1,
				scopedSlots: { title: "custom" },
				index: this.treeLevelSequence(parentIndex, index)
			});
			if (p.level === 1) {
				this.level1Keys.push(p.key);
			}
			if (p.level === 2) {
				this.level2Keys.push(p.key);
			}
			if (p.level === 3) {
				this.level3Keys.push(p.key);
			}

			if (p.children.length) {
				// this.expandedKeys.push(p.key);
				this.formatTreeLevel(p.children, p.index);
			}
		});
	}

	antiFormatTreeLevel(array: ITreeNode[], event: any): void {
		array.forEach((t) => {
			if (t.key === event[0]) {
				t.show = true;
			} else {
				t.show = false;
			}
			if (t.children) {
				this.antiFormatTreeLevel(t.children, event);
			}
		});
	}

	onExpand(expandedKeys: []): void {
		this.expandedKeys = expandedKeys;
		this.autoExpandParent = false;
	}

	setLoading(isLoading) {
		this.isLoading = isLoading;
	}

	setObjective(objective) {
		this.operateTreeNodes(this.treedata, { ...objective, text: pureRichText(objective.text) });
	}

	setUnitObject(value) {
		this.unitObject = value;
	}

	setVisible(value: boolean) {
		this.visible = value;
	}

	doAction(action: any): void {
		switch (parseInt(action, 10)) {
			case TreeActionType.ExpandLevel1:
				this.expandedKeys = [];
				this.autoExpandParent = true;
				break;
			case TreeActionType.ExpandLevel2:
				this.expandedKeys = this.level1Keys;
				this.autoExpandParent = true;
				break;
			case TreeActionType.ExpandLevel3:
				this.expandedKeys = this.level2Keys;
				this.autoExpandParent = true;
				break;
			case TreeActionType.ExpandAll:
				this.expandedKeys = this.level3Keys.concat(this.level2Keys);
				this.autoExpandParent = true;
				break;
			case TreeActionType.NewLevel1:
				this.showDrawer({} as ITreeNode, ObjectiveActionType.New);
				break;
			default:
				return;
		}
	}

	onSelectNode(event: any): void {
		this.antiFormatTreeLevel(this.treedata, event);
	}

	setTreeLevelSequence(array, parentIndex) {
		array.forEach((p, index) => {
			p.index = this.treeLevelSequence(parentIndex, index);
			if (p.children.length) {
				this.setTreeLevelSequence(p.children, p.index);
			}
		});
	}

	newNodeOperate(array, objective, parentNode) {
		let isExcuted = false;
		let formatObj;
		if (objective.level === 1) {
			isExcuted = true;
			formatObj = {
				...objective,
				index: this.treeLevelSequence("", objective.position - 1)
			};
			// array.push(formatObj);
			/* 
				// Instead of pushing the objective at end the objective is inserted at the position user has selected.  
				// This is done so that refresh is not required when creating a new objective to reflect its correct position.
			*/
			array.splice(objective.position - 1, 0, formatObj);
			array.map((node, index) => {
				node.showUpArrow = index != 0;
				node.showDownArrow = index != array.length - 1;
			});
			this.level1Keys.push(formatObj.key);
		} else if (parentNode) {
			isExcuted = true;
			if (!parentNode.children) {
				parentNode.children = [];
			}
			formatObj = {
				...objective,
				index: this.treeLevelSequence(parentNode.index, objective.position - 1)
			};
			// parentNode.children.push(formatObj);
			/* 
				// Instead of pushing the objective at end the objective is inserted at the position user has selected.  
				// This is done so that refresh is not required when creating a new objective to reflect its correct position.
			*/
			parentNode.children.splice(objective.position - 1, 0, formatObj);
			parentNode.children.map((node, index) => {
				node.showUpArrow = index != 0;
				node.showDownArrow = index != parentNode.children.length - 1;
			});
			if (formatObj.level === 2) {
				this.level2Keys.push(formatObj.key);
			}
			if (formatObj.level === 3) {
				this.level3Keys.push(formatObj.key);
			}

			if (!this.expandedKeys.find((k) => k == parentNode.id)) {
				this.expandedKeys.push(parentNode.id);
			}
		}
		return isExcuted;
	}

	deleteNodeOperate(array, currentNode, currentIndex) {
		const parentIndex = currentNode.index.split(".").slice(0, -2).join(".");
		array.splice(currentIndex, 1);
		array.map((node, index) => {
			if (index >= currentIndex) {
				node.showUpArrow = index != 0;
				node.index = this.treeLevelSequence(parentIndex ? `${parentIndex}.` : "", index);
				if (node.children.length) {
					this.setTreeLevelSequence(node.children, node.index);
				}
			} else {
				node.showDownArrow = index != array.length - 1;
			}
		});
	}

	moveUpNodeOperate(array, currentNode) {
		array.reduce((pre, cur, curIndex, array) => {
			if (cur.id == currentNode.id) {
				this.commonOperate(pre, cur, array, curIndex, currentNode.id);
			}
			return cur;
		});
	}

	swapingTreeNodesAPI(pre, cur, swappedId) {
		ObjectiveModule.swapingTreeNodes(
			((pre, cur) => {
				return {
					preId: pre.id,
					curId: cur.id,
					swappedId
				};
			})(pre, cur)
		);
	}

	commonOperateForEditDown(pre, cur, array, curIndex) {
		const tempIndex = pre.index;
		pre.index = cur.index;
		cur.index = tempIndex;
		array.splice(curIndex - 1, 1, cur);
		array.splice(curIndex, 1, pre);
		pre.showUpArrow = true;
		pre.showDownArrow = curIndex != array.length - 1;
		cur.showDownArrow = true;
		cur.showUpArrow = curIndex != 1;
		if (pre.children.length) {
			this.setTreeLevelSequence(pre.children, pre.index);
		}
		if (cur.children.length) {
			this.setTreeLevelSequence(cur.children, cur.index);
		}
	}

	commonOperateForEditUp(pre, cur, array, curIndex) {
		const tempIndex = pre.index;
		pre.index = cur.index;
		cur.index = tempIndex;
		array.splice(curIndex - 1, 1, cur);
		array.splice(curIndex, 1, pre);
		pre.showUpArrow = true;
		pre.showDownArrow = curIndex != array.length - 1;
		cur.showDownArrow = true;
		cur.showUpArrow = curIndex != 1;
		if (pre.children.length) {
			this.setTreeLevelSequence(pre.children, pre.index);
		}
		if (cur.children.length) {
			this.setTreeLevelSequence(cur.children, cur.index);
		}
	}

	commonOperate(pre, cur, array, curIndex, swappedId) {
		this.swapingTreeNodesAPI(pre, cur, swappedId);
		const tempIndex = pre.index;
		pre.index = cur.index;
		cur.index = tempIndex;
		array.splice(curIndex - 1, 1, cur);
		array.splice(curIndex, 1, pre);
		pre.showUpArrow = true;
		pre.showDownArrow = curIndex != array.length - 1;
		cur.showDownArrow = true;
		cur.showUpArrow = curIndex != 1;
		if (pre.children.length) {
			this.setTreeLevelSequence(pre.children, pre.index);
		}
		if (cur.children.length) {
			this.setTreeLevelSequence(cur.children, cur.index);
		}
	}

	moveDownNodeOperate(array, currentNode) {
		array.reduce((pre, cur, curIndex, array) => {
			if (pre.id == currentNode.id) {
				this.commonOperate(pre, cur, array, curIndex, currentNode.id);
			}
			return cur;
		});
	}

	rearrangeNodeLevels(array, currentIndex, parentIndex) {
		array.forEach((node, index) => {
			if (index >= currentIndex) {
				node.showUpArrow = index != 0;
				if (node.children.length) {
					this.setTreeLevelSequence(node.children, node.index);
				}
			} else {
				node.showDownArrow = index != array.length - 1;
			}
		});
	}

	operateTreeNodes(array, objective) {
		let isExcuted = false;
		const currentNode = array.find((a) => a.id === objective.id);
		const currentIndex = array.findIndex((a) => a.id === objective.id);
		const parentNode = array.find((a) => a.id === objective.parentId);
		if (ObjectiveModule.currentActionType == ObjectiveActionType.New) {
			isExcuted = this.newNodeOperate(array, objective, parentNode);
			const parentIndex = parentNode && parentNode.index.split(".").slice(0, -2).join(".");
			// Rearranging levels to reflect correct index of the objectives.
			this.rearrangeNodeLevels(array, currentIndex, parentIndex);
		}
		if (ObjectiveModule.currentActionType == ObjectiveActionType.Edit && currentNode) {
			isExcuted = true;
			Object.assign(currentNode, objective);
			// Rearranging levels to reflect correct index of the objectives.
			const position = objective.position - 1;
			if (currentIndex < position) {
				for (let idx = currentIndex; idx < position; idx++) {
					this.commonOperateForEditDown(array[idx], array[idx + 1], array, idx + 1);
				}
			}
			if (currentIndex > position) {
				for (let idx = currentIndex; idx > position; idx--) {
					this.commonOperateForEditUp(array[idx - 1], array[idx], array, idx);
				}
			}
		}
		if (ObjectiveModule.currentActionType == ObjectiveActionType.Delete && currentIndex > -1) {
			isExcuted = true;
			this.deleteNodeOperate(array, currentNode, currentIndex);
		}
		if (ObjectiveModule.currentActionType == ObjectiveActionType.Up && currentNode) {
			isExcuted = true;
			if (currentIndex == 0) {
				return;
			}
			this.moveUpNodeOperate(array, currentNode);
		}
		if (ObjectiveModule.currentActionType == ObjectiveActionType.Down && currentNode) {
			isExcuted = true;
			if (array.length == currentIndex + 1) {
				return;
			}
			this.moveDownNodeOperate(array, currentNode);
		}
		!isExcuted &&
			array.forEach((a) => {
				if (a.children && a.children.length) {
					this.operateTreeNodes(a.children, objective);
				}
			});
	}

	showDrawer(item: ITreeNode, currentActionType: ObjectiveActionType): void {
		this.setVisible(true);
		this.drawerNode = item;
		ObjectiveModule.setCurrentActionType(currentActionType);
		switch (currentActionType) {
			case ObjectiveActionType.New:
				this.drawerTitle = "New Objective";
				item.isObjectiveIdNew = true;
				item.level == 3 && ObjectiveModule.getUnitobjectiverelatedwithparent(item);
				break;
			case ObjectiveActionType.Edit:
				this.drawerTitle = "Edit Objective";
				item.isObjectiveIdNew = false;
				item.level == 3
					? ObjectiveModule.getUnitobjectiverelated(item)
					: item.level == 4
					? ObjectiveModule.getUnitobjectiverelatedwithparent(item)
					: ObjectiveModule.getRelatedContents(item);
				break;
		}
	}

	movePosition(item: ITreeNode, currentActionType: ObjectiveActionType) {
		ObjectiveModule.setCurrentActionType(currentActionType);
		this.operateTreeNodes(this.treedata, item);
	}

	@Watch("visible")
	deleteObjective(visible) {
		if (!visible && ObjectiveModule.currentActionType == ObjectiveActionType.Delete) {
			this.operateTreeNodes(this.treedata, this.drawerNode);
		}
	}

	onClose(): void {
		this.visible = false;
	}
}
